import React from 'react';
import { Alert, Box, Button, LinearProgress, Typography } from '@mui/material';
import { SourceKey, SourceStatuses, Urgency } from './tasks/types';

/**
 * The dashboard's few colours. Severity is carried by one warm ramp that also
 * differs in lightness, so it reads without colour; programmes and days are
 * never colour-coded.
 */
export const INK = { main: '#0F172A', body: '#334155', muted: '#5B6B80', hairline: '#E3E8EF', faint: '#EEF1F5', ground: '#F6F7F9' };

export const TONES: Record<Urgency | 'done', { color: string; bg: string; dot: string }> = {
  overdue: { color: '#B42318', bg: '#FEF1EF', dot: '#E5483B' },
  soon: { color: '#8A5100', bg: '#FDF3DD', dot: '#E8A317' },
  upcoming: { color: '#475569', bg: '#EEF1F5', dot: '#94A3B8' },
  done: { color: '#475569', bg: '#EEF1F5', dot: '#94A3B8' },
};

export const BAND_LABELS: Record<Urgency, string> = { overdue: 'Overdue', soon: 'Due soon', upcoming: 'Coming up' };

/** Readable by screen readers, invisible on screen. */
export const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

export const panelSx = { bgcolor: '#FFFFFF', border: `1px solid ${INK.hairline}`, borderRadius: '12px' } as const;

export const DueTag: React.FC<{ urgency: Urgency | 'done'; children: React.ReactNode }> = ({ urgency, children }) =>
  children ? (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: '20px',
        px: 1,
        borderRadius: 999,
        color: TONES[urgency].color,
        bgcolor: TONES[urgency].bg,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  ) : null;

const SOURCE_NAMES: Record<SourceKey, string> = {
  checkIns: 'Check-ins',
  success: 'Success updates',
  cleared: 'Cleared alerts',
  reports: 'Reports',
  submissions: 'Assessment submissions',
};

const SOURCE_EFFECT: Record<SourceKey, string> = {
  checkIns: 'clients whose only task is a check-in may be missing',
  success: 'overdue success updates are not shown',
  cleared: 'some alerts shown here may already have been cleared',
  reports: 'report tasks are missing for those clients',
  submissions: 'assessment tasks are missing for those clients',
};

/**
 * Says which sources are still loading or failed, so an empty or short list is
 * never mistaken for "nothing to do".
 */
export const SourceNotice: React.FC<{
  sources: SourceStatuses;
  failures: Partial<Record<SourceKey, number>>;
  onRetry: () => void;
}> = ({ sources, failures, onRetry }) => {
  const keys = Object.keys(sources) as SourceKey[];
  const failed = keys.filter((key) => sources[key] === 'error');
  const loading = keys.filter((key) => sources[key] === 'loading');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {failed.length > 0 ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          {failed.map((key) => {
            const count = failures[key];
            const scope = count ? ` for ${count} client${count === 1 ? '' : 's'}` : '';
            return (
              <Box key={key} component="span" sx={{ display: 'block' }}>
                <strong>{SOURCE_NAMES[key]} didn't load{scope}.</strong> {SOURCE_EFFECT[key].charAt(0).toUpperCase() + SOURCE_EFFECT[key].slice(1)}.
              </Box>
            );
          })}
        </Alert>
      ) : null}
      {loading.length > 0 ? (
        <Box role="status" aria-live="polite">
          <LinearProgress sx={{ height: 2, borderRadius: 1 }} />
          <Typography variant="caption" sx={{ color: INK.muted, display: 'block', mt: 0.5 }}>
            Still checking {loading.map((key) => SOURCE_NAMES[key].toLowerCase()).join(', ')}…
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};
